import { CalendarMonth, ErrorOutline, Hotel, LocationOn, Phone } from '@mui/icons-material';
import { Box, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/system';
import { format, parseISO } from 'date-fns';
import { enUS, frCA } from 'date-fns/locale';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const IconAndTitle = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  gap: 12,
}));

function GoogleMapEmbed() {
  return (
    <div style={{ width: '100%', margin: 'auto' }}>
      <iframe
        sandbox="allow-scripts"
        title="event map"
        src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d11193.525672971164!2d-73.826931!3d45.46212!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc93c6e64a88b4b%3A0x6ba3b8ae04c71c1a!2sDoubleTree%20by%20Hilton%20Pointe%20Claire%20Montreal%20Airport%20West!5e0!3m2!1sen!2sca!4v1716180218989!5m2!1sen!2sca"
        width="100%"
        height="200px"
        allowFullScreen={false}
        loading="lazy"
        style={{
          border: 0,
          filter: 'invert(90%) hue-rotate(180deg)',
        }}
      />
    </div>
  );
}

const formatDateRange = (startAt, endAt, language) => {
  const locale = language === 'en' ? enUS : frCA;
  const startDate = parseISO(startAt);
  const endDate = parseISO(endAt);

  const formattedStartDate = format(startDate, 'MMMM do', { locale });
  const formattedEndDate = format(endDate, 'do yyyy', { locale });

  const formattedStartDateFR = format(startDate, 'd', { locale });
  const formattedEndDateFR = format(endDate, 'd MMMM yyyy', { locale });

  return language === 'en'
    ? `${formattedStartDate} to ${formattedEndDate}`
    : `${formattedStartDateFR} au ${formattedEndDateFR}`;
};

export default function EventInfo({ activeEvent }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const textVariant = 'h6';

  const hotelLink = t('hotel.link');

  return (
    <Paper elevation={2} sx={{ height: 'auto' }}>
      <GoogleMapEmbed />
      <Box
        sx={[
          {
            padding: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflow: 'auto',
          },
          !isMobile && { height: 'calc(100vh - 200px - 64px)' },
        ]}
      >
        {/* Date */}
        <Box>
          <IconAndTitle>
            <CalendarMonth color="primary" fontSize="large" />
            <Typography variant={textVariant} color="primary" className="font-bebas">
              {t('info.event')}
            </Typography>
          </IconAndTitle>
          <Box sx={{ marginLeft: 6 }}>
            <Typography variant="subtitle2" className="font-roboto font-bold uppercase">
              {formatDateRange(activeEvent.startAt, activeEvent.endAt, i18n.language)}
            </Typography>
          </Box>
        </Box>

        {/* Hotel */}
        <Box>
          <IconAndTitle>
            <LocationOn color="primary" fontSize="large" />
            <Typography variant={textVariant} color="primary" className="font-bebas">
              {t('info.location')}
            </Typography>
          </IconAndTitle>
          <Box sx={{ marginLeft: 6 }}>
            <Typography
              target="_blank"
              href={hotelLink}
              component={hotelLink ? 'a' : 'div'}
              variant="subtitle2"
              className="font-roboto font-bold uppercase"
            >
              {t('info.hotel')}
            </Typography>
            <Typography variant="subtitle2" className="font-roboto" sx={{ marginTop: 1 }}>
              {t('info.address')}
            </Typography>
            <Typography variant="subtitle2" className="font-roboto">
              Québec, Canada
            </Typography>
            <Typography variant="subtitle2" className="font-roboto">
              H9R 1C2
            </Typography>
          </Box>
        </Box>
        {/* TODO: Link this info to event https://github.com/NoResetSpeedrun/NoResetv3/issues/374 */}
        {hotelLink && (
          <>
            {/* Price */}
            <Box>
              <IconAndTitle>
                <Hotel color="primary" fontSize="large" />
                <Typography variant={textVariant} color="primary" className="font-bebas">
                  {t('info.room')}
                </Typography>
              </IconAndTitle>
              <Box sx={{ marginLeft: 6 }}>
                <Typography
                  gutterBottom
                  variant="subtitle2"
                  className="font-roboto font-bold uppercase"
                >
                  {t('info.room2')}
                </Typography>
                <Typography variant="subtitle2" className="font-roboto">
                  {t('info.room3')}
                </Typography>
                <Typography variant="subtitle2" className="font-roboto">
                  {t('info.room4')}
                </Typography>
                <Typography variant="subtitle2" className="font-roboto">
                  {t('info.room5')}
                </Typography>
              </Box>
            </Box>

            {/* Phone */}
            <Box>
              <IconAndTitle>
                <Phone color="primary" fontSize="large" />
                <Typography variant={textVariant} color="primary" className="font-bebas">
                  {t('info.contact')}
                </Typography>
              </IconAndTitle>
              <Box sx={{ marginLeft: 6 }}>
                <Typography
                  variant="subtitle2"
                  className="font-roboto underline"
                  component="a"
                  href="tel:+15146977110"
                >
                  +1 (514) 697 - 7110
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* EXTRA INTO */}
        <Box>
          <IconAndTitle>
            <ErrorOutline color="primary" fontSize="large" />
            <Typography variant={textVariant} color="primary" className="font-bebas">
              {t('info.other.title')}
            </Typography>
          </IconAndTitle>
          <Box sx={{ marginLeft: 6 }}>
            <Typography variant="subtitle2" className="font-roboto">
              {t('info.other.submissions')}
            </Typography>
            <Typography variant="subtitle2" className="font-roboto">
              {t('info.other.volunteer')}
            </Typography>
            <Typography variant="subtitle2" className="font-roboto">
              {t('info.other.raffles')}
            </Typography>
            <Typography variant="subtitle2" className="font-roboto">
              {t('info.other.host')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

EventInfo.propTypes = {
  activeEvent: PropTypes.object.isRequired,
};
